import './UserGuide.css';

/**
 * User Guide — AI Readiness.
 *
 * DESIGN CLONED from Inventory Registry's User Guide, which the owner named as the pattern
 * (2026-08-11: "Use APR module for both of these as a reference … This should be the same
 * design"). Same TOC, same cards, same tick-lists; the class prefix is `airug-` because those
 * rules are unscoped and stylesheets are global once bundled (CLAUDE.md §1.2.1b).
 *
 * ── WHERE THE CONTENT COMES FROM, AND WHERE IT DOES NOT ────────────────────────────────
 *
 * Two sections are the source's own words:
 *   · "Strategic Intelligence & Discovery" — `views/user-guide.ejs`:840-868, verbatim.
 *   · The five-step lifecycle — `views/start-menu.ejs`, already cloned into this module's
 *     own StartPage.tsx, quoted from there rather than re-derived.
 *
 * THE PLATFORM-FIT SCREENS HAVE NO SOURCE GUIDE ENTRY. `AI Platforms Catalog`, `Platform
 * Analysis`, `Platform Fitment Criteria`, `AI Platform Fit` and `Platform Fit Summary` do not
 * appear anywhere in the source's 1,095-line user guide — checked by name, all five absent.
 * They post-date it. Their descriptions below are therefore WRITTEN, not cloned, and describe
 * what the screens in this module actually do.
 *
 * That distinction is recorded here rather than smoothed over, because everything else in
 * this port is traceable to a source line and this is not. Raised with the owner 2026-08-11:
 * if the source app grows a guide entry for these screens, this text should be replaced by
 * it rather than kept.
 */

interface FeatureCard {
  icon: string;
  title: string;
  body: string;
  points?: string[];
}

interface Section {
  id: string;
  icon: string;
  label: string;
  heading: string;
  intro?: string;
  features?: FeatureCard[];
  steps?: { n: string; title: string; desc: string }[];
  /** Marks a section whose prose is authored here because the source has none. */
  authored?: boolean;
}

const SECTIONS: Section[] = [
  {
    id: 'lifecycle',
    icon: '🚀',
    label: 'The five steps',
    heading: 'How AI Readiness works',
    intro:
      'Score every initiative against one consistent framework, weigh its benefit and its ' +
      'cost, sequence what survives into a roadmap, and run delivery. Each step below has its ' +
      'own screens, reachable from the sidebar or the Start Menu.',
    steps: [
      { n: '1', title: 'Score — objectively', desc: 'One framework for every initiative, so priority reflects evidence rather than the loudest voice.' },
      { n: '2', title: 'Weigh the benefit', desc: 'Put a number on savings, ROI and value realised over time.' },
      { n: '3', title: 'Weigh the cost', desc: 'Model build and run cost, then set benefit against cost to decide.' },
      { n: '4', title: 'Shape the roadmap', desc: 'Sequence the chosen work into phases, dependencies and timelines.' },
      { n: '5', title: 'Run delivery', desc: 'Define the programs, lock the timeline, then track progress against plan.' },
    ],
  },
  {
    id: 'strategic-intelligence',
    icon: '🧠',
    label: 'Strategic Intelligence',
    heading: 'Strategic Intelligence & Discovery',
    intro:
      'Strategic Intelligence is where initiatives originate. Analyze companies, generate ' +
      'candidate AI opportunities, and define the assessment criteria that drive scoring. ' +
      'Available to Administrators and Strategic Analysts.',
    features: [
      {
        icon: 'travel_explore',
        title: 'Strategic Discoveries',
        body:
          'Run AI-assisted discovery against a company to surface candidate initiatives, use ' +
          'cases, and value hypotheses.',
        points: [
          'Capture the company profile and strategic context',
          'Generate candidate opportunities from discovery sessions',
          'Promote discoveries into managed opportunities',
        ],
      },
      {
        icon: 'tune',
        title: 'Scoring Frameworks',
        body:
          'Define the dimensions, questions, and weights that determine how every opportunity ' +
          'is evaluated.',
        points: [
          'Configure dimensions and sub-criteria',
          'Manage the question & answer catalog',
          'Tune weights to reflect strategy',
        ],
      },
    ],
  },
  {
    id: 'platform-fit',
    icon: '🧭',
    label: 'AI Platform Fit',
    heading: 'Choosing the AI platform',
    intro:
      'These screens decide WHICH AI platform an opportunity should be delivered on, and why.',
    authored: true,
    features: [
      {
        icon: 'inventory_2',
        title: 'AI Platforms Catalog',
        body:
          'The catalogue of AI platforms under consideration, with the profile held for each ' +
          'one. Everything downstream scores against this list, so a platform has to exist ' +
          'here before it can be compared.',
      },
      {
        icon: 'auto_awesome',
        title: 'Platform Analysis',
        body:
          'AI-assisted analysis of a platform, building the profile the fitment scoring reads.',
      },
      {
        icon: 'play_circle',
        title: 'Program Quick Start',
        body:
          'Upload a client plan — a deck, a workbook, a report, a screenshot — and the analysis ' +
          'proposes the projects, opportunities, dates, work items and RACI it can read from ' +
          'it. Nothing is created until you press Generate; the screen only builds a preview ' +
          'you select from, and it cannot invent records that are not in the file.',
      },
      {
        icon: 'rule',
        title: 'Platform Fitment Criteria',
        body:
          'The criteria and weights that decide how well a platform fits an opportunity — the ' +
          'equivalent of a scoring framework, for platforms rather than initiatives.',
      },
      {
        icon: 'analytics',
        title: 'AI Platform Fit',
        body: 'Scores each opportunity against each platform using those criteria.',
      },
      {
        icon: 'insights',
        title: 'Platform Fit Summary',
        body: 'The portfolio-level view of those results — which platform suits which work.',
      },
    ],
  },
  {
    id: 'settings',
    icon: '⚙️',
    label: 'Settings',
    heading: 'Settings',
    authored: true,
    features: [
      {
        icon: 'tune',
        title: 'AI Platform Selection',
        body:
          'Holds the selection criteria and the fit results behind the Strategic Intelligence ' +
          'screens. Change the criteria here and the fit scores are recalculated against them.',
      },
    ],
  },
];

export function UserGuide() {
  return (
    <div className="air-uguide">
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">
              User Guide<span className="airug-badge">v2.0</span>
            </h1>
            <p className="page-header__subtitle">
              Complete guide to assessing AI readiness and platform fit
            </p>
          </div>
          <div className="page-header__actions" />
        </div>
      </div>

      <div className="airug-toc">
        <p className="airug-toc-title">Quick Navigation</p>
        <ul className="airug-toc-list">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>
                <span aria-hidden="true">{s.icon}</span>
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {SECTIONS.map((s) => (
        <section key={s.id} id={s.id} className="airug-card">
          <h2>{s.heading}</h2>
          {s.intro ? <p>{s.intro}</p> : null}

          {s.steps ? (
            <div className="airug-steps">
              {s.steps.map((q) => (
                <div className="airug-step" key={q.n}>
                  <div className="airug-step-n">{q.n}</div>
                  <div className="airug-step-t">{q.title}</div>
                  <div className="airug-step-d">{q.desc}</div>
                </div>
              ))}
            </div>
          ) : null}

          {s.features ? (
            <div className="airug-features">
              {s.features.map((f) => (
                <div className="airug-feature" key={f.title}>
                  <div className="airug-feature-hd">
                    <i className="material-icons">{f.icon}</i>
                    <h3>{f.title}</h3>
                  </div>
                  <p>{f.body}</p>
                  {f.points ? (
                    <ul className="airug-ticks">
                      {f.points.map((p) => (
                        <li key={p}>
                          <i className="material-icons">check_circle</i>
                          {p}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}
